/**
 * Winess Interactive Carousel — Web Component
 * Infinite cover-flow carousel with arrow nav, touch/swipe, keyboard support.
 * Clones first/last slides for seamless looping.
 */
if (!customElements.get('winess-interactive-carousel')) {
  class WinessInteractiveCarousel extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = 0;
      this.realCount = 0;
      this.touchStartX = 0;
      this.touchDeltaX = 0;
      this.isDragging = false;
      this.isTransitioning = false;
    }

    connectedCallback() {
      this.realCount = parseInt(this.dataset.slideCount, 10) || 0;
      if (this.realCount < 1) return;

      this.track = this.querySelector('.winess-carousel__track');
      this.dots = this.querySelectorAll('.winess-carousel__dot');
      this.prevBtn = this.querySelector('.winess-carousel__arrow--prev');
      this.nextBtn = this.querySelector('.winess-carousel__arrow--next');

      // Clone first and last slides for infinite loop
      this.setupClones();

      this.allSlides = this.querySelectorAll('.winess-carousel__slide');

      this.prevBtn?.addEventListener('click', () => this.goTo(this.currentIndex - 1));
      this.nextBtn?.addEventListener('click', () => this.goTo(this.currentIndex + 1));

      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          this.goTo(parseInt(dot.dataset.dotIndex, 10));
        });
      });

      // Snap to real slide after animating to a clone
      this.track.addEventListener('transitionend', () => this.onTransitionEnd());

      // Touch events
      this.addEventListener('touchstart', this.onTouchStart.bind(this), { passive: true });
      this.addEventListener('touchmove', this.onTouchMove.bind(this), { passive: false });
      this.addEventListener('touchend', this.onTouchEnd.bind(this), { passive: true });

      // Keyboard
      this.addEventListener('keydown', this.onKeyDown.bind(this));

      // Click on non-active slide to navigate
      this.allSlides.forEach((slide, i) => {
        slide.addEventListener('click', () => {
          // Map DOM index back to real index (offset by 1 due to prepended clone)
          const realIdx = i - 1;
          if (realIdx !== this.currentIndex) this.goTo(realIdx);
        });
      });

      // Start at first real slide (DOM index 1)
      this.goTo(0, true);
    }

    setupClones() {
      const slides = this.track.querySelectorAll('.winess-carousel__slide');
      if (slides.length < 1) return;

      // Clone last slide → prepend before first
      const lastClone = slides[slides.length - 1].cloneNode(true);
      lastClone.classList.add('is-clone');
      lastClone.removeAttribute('data-slide-index');
      this.track.insertBefore(lastClone, slides[0]);

      // Clone first slide → append after last
      const firstClone = slides[0].cloneNode(true);
      firstClone.classList.add('is-clone');
      firstClone.removeAttribute('data-slide-index');
      this.track.appendChild(firstClone);
    }

    goTo(index, instant = false) {
      if (this.isTransitioning && !instant) return;

      this.currentIndex = index;

      // DOM index: offset by 1 because of prepended clone
      const domIndex = index + 1;

      const slideWidthPercent = this.getSlideWidthPercent();
      const centerOffset = (100 - slideWidthPercent) / 2;
      const translateX = -(domIndex * slideWidthPercent) + centerOffset;

      if (instant) {
        this.track.style.transition = 'none';
      } else {
        this.track.style.transition = 'transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
        this.isTransitioning = true;
      }

      this.track.style.transform = `translateX(${translateX}%)`;

      if (instant) {
        this.track.offsetHeight; // force reflow
        this.track.style.transition = '';
      }

      this.updateActiveStates();
    }

    onTransitionEnd() {
      this.isTransitioning = false;

      // If we're on a clone, snap to the corresponding real slide
      if (this.currentIndex < 0) {
        // Went past the start → snap to last real slide
        this.goTo(this.realCount - 1, true);
      } else if (this.currentIndex >= this.realCount) {
        // Went past the end → snap to first real slide
        this.goTo(0, true);
      }
    }

    updateActiveStates() {
      // Determine the visual index (wrapped to real range) for styling
      let visualIndex = this.currentIndex;
      if (visualIndex < 0) visualIndex = this.realCount - 1;
      if (visualIndex >= this.realCount) visualIndex = 0;

      // DOM index offset by 1 for the prepended clone
      const activeDomIndex = this.currentIndex + 1;

      this.allSlides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === activeDomIndex);
      });

      this.dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === visualIndex);
        dot.setAttribute('aria-selected', i === visualIndex ? 'true' : 'false');
      });
    }

    getSlideWidthPercent() {
      const vw = window.innerWidth;
      if (vw >= 990) return 65;
      if (vw >= 750) return 60;
      return 100;
    }

    onTouchStart(e) {
      this.isDragging = true;
      this.touchStartX = e.touches[0].clientX;
      this.touchDeltaX = 0;
    }

    onTouchMove(e) {
      if (!this.isDragging) return;
      this.touchDeltaX = e.touches[0].clientX - this.touchStartX;
      if (Math.abs(this.touchDeltaX) > 10) {
        e.preventDefault();
      }
    }

    onTouchEnd() {
      if (!this.isDragging) return;
      this.isDragging = false;

      const threshold = 50;
      if (this.touchDeltaX > threshold) {
        this.goTo(this.currentIndex - 1);
      } else if (this.touchDeltaX < -threshold) {
        this.goTo(this.currentIndex + 1);
      }
    }

    onKeyDown(e) {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.goTo(this.currentIndex - 1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.goTo(this.currentIndex + 1);
      }
    }
  }

  customElements.define('winess-interactive-carousel', WinessInteractiveCarousel);
}
